/**
 * web-studio-sites D7 — patches a placeholder's content to a drawing's new
 * markup in place, so the elements that stay are the same elements and focus,
 * scroll and a field being typed in survive a redraw. An element is matched by
 * its `id`, else its `data-key`, else its part and `data-item` (a list entry
 * keeps its element when the list reorders), else by position and tag.
 */

function keyOf(node: Node): string | null {
  if (!(node instanceof Element)) return null;
  if (node.id) return `#${node.id}`;
  const key = node.getAttribute('data-key');
  if (key !== null) return `key:${key}`;
  const item = node.getAttribute('data-item');
  return item !== null ? `item:${node.getAttribute('data-part') ?? ''}:${item}` : null;
}

function sameKind(a: Node, b: Node): boolean {
  return a.nodeType === b.nodeType && (!(a instanceof Element) || a.tagName === (b as Element).tagName);
}

function syncAttributes(from: Element, to: Element): void {
  for (const { name } of [...from.attributes]) if (!to.hasAttribute(name)) from.removeAttribute(name);
  for (const { name, value } of [...to.attributes]) if (from.getAttribute(name) !== value) from.setAttribute(name, value);
}

/** A field's typed value and check stay while the visitor is in it. */
function syncField(from: Element, to: Element): void {
  if (from === document.activeElement) return;
  if (from instanceof HTMLInputElement && to instanceof HTMLInputElement) {
    if (from.value !== to.value) from.value = to.value;
    if (from.checked !== to.checked) from.checked = to.checked;
  } else if (from instanceof HTMLTextAreaElement && to instanceof HTMLTextAreaElement) {
    if (from.value !== to.value) from.value = to.value;
  }
}

function update(from: Node, to: Node): void {
  if (!(from instanceof Element) || !(to instanceof Element)) {
    if (from.nodeValue !== to.nodeValue) from.nodeValue = to.nodeValue;
    return;
  }
  syncAttributes(from, to);
  syncField(from, to);
  if (!(from instanceof HTMLTextAreaElement)) morphChildren(from, to);
}

function morphChildren(from: Node, to: Node): void {
  const keyed = new Map<string, Node>();
  for (const child of from.childNodes) {
    const key = keyOf(child);
    if (key !== null) keyed.set(key, child);
  }
  let cursor: ChildNode | null = from.firstChild;
  for (const next of [...to.childNodes]) {
    const key = keyOf(next);
    let match: Node | null = null;
    if (key !== null) {
      const old = keyed.get(key);
      if (old && sameKind(old, next)) {
        match = old;
        keyed.delete(key);
      }
    } else if (cursor && keyOf(cursor) === null && sameKind(cursor, next)) {
      match = cursor;
    }
    if (!match) {
      from.insertBefore(next, cursor);
      continue;
    }
    if (match === cursor) cursor = cursor.nextSibling;
    else from.insertBefore(match, cursor);
    update(match, next);
  }
  while (cursor) {
    const after: ChildNode | null = cursor.nextSibling;
    from.removeChild(cursor);
    cursor = after;
  }
}

/** Makes `target`'s content the given markup, keeping every element that stays. */
export function morph(target: Element, markup: string): void {
  const template = document.createElement('template');
  template.innerHTML = markup;
  morphChildren(target, template.content);
}
